import {Injectable, Injector} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {Observable} from 'rxjs';
import {Action, select, Store} from '@ngrx/store';
import {TestBarEnableConfirmButtonAction} from '@ispm/test/store/test-bar.actions';
import {selectActiveJob, selectActiveJobPurpose} from '@ispm/workflow/store/workflow-config.selectors';
import {IgenomicsState} from '@ispm/core/interfaces/genomics-state.interface';
import {concatMap, filter, map, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import {IDiagnosticOrder} from '@ispm/test/interfaces/diagnostic-order.interface';
import {
  TestBarEnableConfirmButtonForJobAction,
  TestBarForJobActionTypes,
  TestBarMoveToNextJobAction,
  UpdateResourcesAction
} from '@ispm/shared/store/actions/test-bar.actions';
import {PollJobService} from '@ispm/test/services/poll-job.service';
import {selectUiStepOverrideConfiguration} from '../../../configuration/store/configuration.selector';
import {get} from "lodash";
import {Resources} from "@ispm/workflow/constants/resources.enum";
import {setTreatmentsActionCreator, setTrialsActionCreator} from "@ispm/therapies/store/therapies.actions";
import {setActiveWorkflow, setResourceMap} from "@ispm/workflow/store/workflow-config.actions";
import {EmrIntegrationService} from "@ispm/test/services/emr-integration.service";
import { WorklistNavigationService } from '../services/worklist-navigation.service';


@Injectable()
export class TestBarEffects {

  private diagnosticOrder$: Observable<IDiagnosticOrder> =
    this.store$.select('tests', 'diagnosticOrders', 'order');

  constructor(private actions$: Actions,
              private store$: Store<IgenomicsState>,
              private pollJobService: PollJobService,
              private emrIntegrationService: EmrIntegrationService,
              private injector: Injector,
    ) {
  }

  private getWorklistNavigationService(): WorklistNavigationService {
    return this.injector.get(WorklistNavigationService);
  }

  @Effect()
  filterSetEnabledByCurrentJob$: Observable<Action> = this.actions$.pipe(
    ofType<TestBarEnableConfirmButtonForJobAction>(TestBarForJobActionTypes.EnableConfirmButtonForJob),
    withLatestFrom(this.store$.pipe(select(selectActiveJobPurpose))),
    filter(([action, activeJobPurpose]) => action.payload.jobPurpose === activeJobPurpose),
    switchMap(([action, activeJobPurpose]) => [new TestBarEnableConfirmButtonAction(action.payload.enable)])
  );

  @Effect({ dispatch: false})
  moveToNextWorkflowStep$ = this.actions$.pipe(
    ofType<TestBarMoveToNextJobAction>(TestBarForJobActionTypes.MoveToNextJob),
    withLatestFrom(
      this.store$.pipe(select(selectActiveJob))
    ),
    filter(([action, activeJobItem]) => action.payload.jobPurpose === activeJobItem.purpose),
    withLatestFrom(
      this.diagnosticOrder$
    ),
    switchMap(res => {
      return this.store$.pipe(
        select(selectUiStepOverrideConfiguration('Test')),
        take(1),
        map((uiStepOverrides) =>  ([...res, uiStepOverrides]))
      )
    }),
    tap(([[action, activeJobItem], currentTest, uiStepOverrides]) =>
      {
        this.pollJobService.performWorkflowStepByStepName(
        activeJobItem.name,
        action.payload.params,
        currentTest,
        action.payload.targetStepForPolling,
        action.payload.flowConfig,
        get(uiStepOverrides,'workflowAction'),
        (resourceData) => {
          const updatedWorkflow = resourceData.length > 0 ? resourceData[0].resource : resourceData.resource;
          this.emrIntegrationService.redirectToEMRIntegration(action.payload.emrIntegration, currentTest,updatedWorkflow);
          this.getWorklistNavigationService().navigateToWorklist();
        },
        true,
        action.payload.actionToDispatchOnError
        );
      })
  );

  @Effect()
  updateResources$ = this.actions$.pipe(
    ofType<UpdateResourcesAction>(TestBarForJobActionTypes.UpdateResources),
    concatMap(action => {
      const items = action.payload || [];
      return items.reduce((arr, item) => {
        let actionItem;
        if(item.resourceType){
          switch (item.resourceType.toLowerCase()) {
            case Resources.WorkflowRequest.toLowerCase():
              actionItem = setActiveWorkflow(item.resource);
              break;
            case Resources.ResourceMap.toLowerCase():
              actionItem = setResourceMap(item.resource);
              break;
            case Resources.TreatmentPlan.toLowerCase():
              actionItem = setTreatmentsActionCreator(item.resource);
              break;
            case Resources.ClinicalTrialMatch.toLowerCase():
              actionItem = setTrialsActionCreator(item.resource);
              break;
            default:
              return arr;
          }
          arr.push(actionItem);
        }
        return arr;
      }, [])

    })
  )
}



